import { Link } from "react-router-dom";
import type { Clue } from "@/mocks/clueData";

type Props = {
  clue: Clue;
  variant?: "default" | "compact" | "horizontal";
};

function formatViews(value: number) {
  return value >= 10000 ? `${(value / 10000).toFixed(1)} 万` : `${value}`;
}

export default function ClueCard({ clue, variant = "default" }: Props) {
  if (variant === "horizontal") {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row">
        <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
          <img
            src={clue.image}
            alt={clue.imageAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2 text-xs text-foreground-600">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900">
              {clue.categoryName}
            </span>
            <span>{clue.date}</span>
          </div>
          <h3 className="mt-2 line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950">
            <Link to={`/clues/${clue.slug}`} className="hover:text-primary-700">
              {clue.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-foreground-600">
            {clue.excerpt}
          </p>
          <div className="mt-auto flex items-center gap-4 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(clue.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {clue.comments}
            </span>
          </div>
        </div>
      </article>
    );
  }

  const isCompact = variant === "compact";

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      {!isCompact && (
        <div className="h-48 w-full overflow-hidden bg-background-200">
          <img
            src={clue.image}
            alt={clue.imageAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className={`flex flex-1 flex-col ${isCompact ? "p-4" : "p-5"}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
            {clue.categoryName}
          </span>
          {clue.hot && (
            <span className="flex items-center gap-1 text-xs font-medium text-primary-700">
              <i className="ri-fire-line"></i>
              热议
            </span>
          )}
        </div>
        <h3
          className={`mt-3 line-clamp-2 font-heading font-semibold leading-snug text-foreground-950 ${
            isCompact ? "text-base" : "text-lg"
          }`}
        >
          <Link to={`/clues/${clue.slug}`} className="hover:text-primary-700">
            {clue.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{clue.excerpt}</p>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {clue.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(clue.views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {clue.readTime}
          </span>
        </div>
      </div>
    </article>
  );
}